import { SquareCheckBig } from 'lucide-react';
import { Modal } from '@/components/ui/Modal';

export interface ConfirmWithoutDateModalProps {
  /** Whether the modal is shown. */
  open: boolean;
  /** The undated tasks to list — only the title is rendered (with a task icon). */
  tasks: { id: string; title: string }[];
  /** Primary CTA — dismiss the modal and return to Review Items; nothing is confirmed. */
  onAddDates: () => void;
  /** Secondary CTA — confirm all items; undated tasks route to Today → Unscheduled. */
  onConfirmWithoutDate: () => void;
  /** Backdrop/escape handler. Defaults to `onAddDates` (closing == returning to review). */
  onClose?: () => void;
  /** Disables both CTAs while the confirm request is in flight. */
  loading?: boolean;
}

/**
 * "Confirm without a date?" — shown when the user taps Confirm while one or
 * more tasks still lack a date (Path B). Lists the undated tasks by title and
 * task icon only; offers to go back and date them ("Add dates") or commit
 * anyway ("Confirm without date").
 */
export function ConfirmWithoutDateModal({
  open,
  tasks,
  onAddDates,
  onConfirmWithoutDate,
  onClose,
  loading = false,
}: ConfirmWithoutDateModalProps) {
  return (
    <Modal
      open={open}
      onClose={onClose ?? onAddDates}
      ariaLabel="Confirm without a date?"
      bare
    >
      <div className="flex flex-col items-center gap-4 p-5">
        <div className="flex flex-col gap-1.5 text-center w-full">
          <p className="text-xl font-medium text-foreground m-0">
            Confirm without a date?
          </p>
          <p className="text-base font-normal text-foreground m-0">
            These tasks will only be saved to Today. You can add a due date
            later.
          </p>
        </div>

        <div className="flex flex-col gap-4 w-full py-1 max-h-[40dvh] overflow-y-auto">
          {tasks.map((task) => (
            <div
              key={task.id}
              className="flex items-center gap-2"
            >
              <SquareCheckBig
                size={24}
                className="text-muted-foreground flex-shrink-0"
              />
              <span className="text-base font-medium text-foreground">
                {task.title}
              </span>
            </div>
          ))}
        </div>

        <div className="flex flex-col gap-3 w-full">
          <button
            type="button"
            onClick={onAddDates}
            disabled={loading}
            className="w-full flex items-center justify-center rounded-md bg-primary py-2 px-4 text-base font-semibold text-primary-foreground cursor-pointer border-none disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Add dates
          </button>
          <button
            type="button"
            onClick={onConfirmWithoutDate}
            disabled={loading}
            className="w-full py-1.5 text-base font-semibold text-muted-foreground bg-transparent border-none cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Confirm without date
          </button>
        </div>
      </div>
    </Modal>
  );
}
